import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Braces,
  Clock3,
  Edit3,
  ExternalLink,
  Gauge,
  Hash,
  Layers3,
  Lock,
  Music2,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Unlock,
  Youtube,
} from "lucide-react";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import {
  deleteSongV2,
  fetchSongV2ById,
  SongV2,
} from "./songV2Api";
import styles from "./SongV2Shared.module.css";

const formatter = new Intl.DateTimeFormat("tr-TR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : formatter.format(date);
}

function formatDuration(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

export default function SongV2Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const access = getCurrentTokenAccessInfo();
  const canWrite = Boolean(access?.hasPanelAccess);
  const canDelete = Boolean(access?.canDeleteSongs);
  const [song, setSong] = useState<SongV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [message] = useState(
    (location.state as { message?: string } | null)?.message || ""
  );
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [dataTab, setDataTab] = useState<"dt" | "m" | "chart">("chart");

  const loadSong = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      setSong(await fetchSongV2ById(id));
    } catch (err: any) {
      setError(err?.message || "V2 şarkı detayı yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSong();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const remove = async () => {
    if (!song || !canDelete) return;
    try {
      setWorking(true);
      await deleteSongV2(song.songv2_id);
      navigate("/songs", { replace: true });
    } catch (err: any) {
      setError(err?.message || "Şarkı silinemedi.");
      setDeleteOpen(false);
      setWorking(false);
    }
  };

  const chartData = useMemo(() => {
    if (!song) return "";
    if (dataTab === "dt") return JSON.stringify(song.dt, null, 2);
    if (dataTab === "m") return JSON.stringify(song.m, null, 2);
    return JSON.stringify(song.chart, null, 2);
  }, [dataTab, song]);

  if (loading) {
    return <div className={styles.songState}><div className={styles.songLoader} /><h2>Tam V2 kayıt yükleniyor</h2><p>Lane, dt, m ve chart verileri hazırlanıyor.</p></div>;
  }

  if (!song) {
    return <div className={styles.songState}><AlertCircle size={32} /><h2>Şarkı bulunamadı</h2><p>{error || "V2 kayıt mevcut değil."}</p><Link to="/songs"><ArrowLeft size={16} /> Listeye dön</Link></div>;
  }

  return (
    <div className={styles.songPage}>
      <header className={styles.detailHero}>
        <Link to="/songs"><ArrowLeft size={17} /></Link>
        <div className={styles.detailIdentity}>
          <span><Music2 size={22} /></span>
          <div>
            <div>
              <span className={styles.v2Badge}>V{song.v}</span>
              {song.is_locked ? <small><Lock size={12} /> Kilitli</small> : <small><Unlock size={12} /> Açık</small>}
            </div>
            <h1>{song.title}</h1>
            <p>{song.artist}</p>
          </div>
        </div>
        <div className={styles.detailActions}>
          <button type="button" onClick={loadSong}><RefreshCw size={16} /></button>
          {canWrite && <Link to={`/songs/edit/${song.songv2_id}`}><Edit3 size={16} /> Düzenle</Link>}
          {canDelete && <button type="button" className={styles.detailDelete} onClick={() => setDeleteOpen(true)}><Trash2 size={16} /></button>}
        </div>
      </header>

      {(error || message) && (
        <div className={error ? styles.songFeedbackError : styles.songFeedbackSuccess}>
          {error ? <AlertCircle size={17} /> : <ShieldCheck size={17} />}<span>{error || message}</span>
        </div>
      )}

      <section className={styles.detailStats}>
        <article><span><Gauge size={17} /></span><div><small>Tempo</small><strong>{song.bpm} BPM</strong></div></article>
        <article><span><Clock3 size={17} /></span><div><small>Süre</small><strong>{formatDuration(song.duration_ms)}</strong></div></article>
        <article><span><Layers3 size={17} /></span><div><small>Lane / event</small><strong>{song.lanes.length} / {song.dt.length}</strong></div></article>
        <article><span><SlidersHorizontal size={17} /></span><div><small>Sync / hız</small><strong>{song.sync_ms} ms / {song.calibrate_speed}x</strong></div></article>
      </section>

      <div className={styles.detailGrid}>
        <section className={styles.songPanel}>
          <div className={styles.panelHeading}><div><span>Kaynak</span><h2>Medya bağlantısı</h2></div><Youtube size={18} /></div>
          <div className={styles.sourceCard}>
            <span><Youtube size={22} /></span>
            <div><small>{song.source.type.toUpperCase()} · {song.source.video_id || "ID YOK"}</small><strong>{song.source.url || "Kaynak URL bulunmuyor"}</strong></div>
            {song.source.url && <a href={song.source.url} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} /> Aç</a>}
          </div>
          {song.source.type === "youtube" && song.source.video_id && (
            <div className={styles.videoFrame}>
              <iframe
                src={`https://www.youtube.com/embed/${encodeURIComponent(song.source.video_id)}`}
                title={`${song.title} video`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </section>

        <section className={styles.songPanel}>
          <div className={styles.panelHeading}><div><span>V2 kayıt</span><h2>Sistem ve oynatma alanları</h2></div><Hash size={18} /></div>
          <div className={styles.apiGrid}>
            <div><span>songv2_id</span><strong>{song.songv2_id}</strong></div>
            <div><span>Versiyon</span><strong>{song.v}</strong></div>
            <div><span>Time signature</span><strong>{song.ts}</strong></div>
            <div><span>Duration</span><strong>{song.duration_ms} ms</strong></div>
            <div><span>Sync</span><strong>{song.sync_ms} ms</strong></div>
            <div><span>Lookahead</span><strong>{song.lookahead_ms} ms</strong></div>
            <div><span>Hit window</span><strong>{song.hit_ms} ms</strong></div>
            <div><span>Calibrate speed</span><strong>{song.calibrate_speed}</strong></div>
            <div><span>Oluşturuldu</span><strong>{formatDate(song.created_at)}</strong></div>
            <div><span>Güncellendi</span><strong>{formatDate(song.updated_at)}</strong></div>
          </div>
        </section>

        <section className={`${styles.songPanel} ${styles.notesPanel}`}>
          <div className={styles.panelHeading}><div><span>Davul haritası</span><h2>Lane tanımları</h2></div><Layers3 size={18} /></div>
          <div className={styles.laneGrid}>
            {song.lanes.map((lane) => (
              <article key={`${lane.lane}-${lane.name}`}>
                <span>{lane.lane}</span>
                <div><strong>{lane.name}</strong><small>MIDI: {lane.midi_notes.join(", ") || "—"}</small></div>
              </article>
            ))}
          </div>
        </section>

        <section className={`${styles.songPanel} ${styles.notesPanel}`}>
          <div className={styles.panelHeading}>
            <div><span>Ham V2 içerik</span><h2>Chart veri denetleyicisi</h2></div>
            <div className={styles.dataTabs}>
              <button type="button" className={dataTab === "chart" ? styles.dataTabActive : ""} onClick={() => setDataTab("chart")}>chart ({song.chart.events.length})</button>
              <button type="button" className={dataTab === "dt" ? styles.dataTabActive : ""} onClick={() => setDataTab("dt")}>dt ({song.dt.length})</button>
              <button type="button" className={dataTab === "m" ? styles.dataTabActive : ""} onClick={() => setDataTab("m")}>m ({song.m.length})</button>
            </div>
          </div>
          <div className={styles.integrityLine}>
            <Braces size={14} />
            <span>dt/m bütünlüğü</span>
            <strong className={song.dt.length === song.m.length ? styles.integrityOk : styles.integrityError}>
              {song.dt.length === song.m.length ? "Geçerli" : "Uzunluklar farklı"}
            </strong>
          </div>
          <pre>{chartData}</pre>
        </section>
      </div>

      {deleteOpen && (
        <div className={styles.songModalOverlay} role="presentation" onMouseDown={() => !working && setDeleteOpen(false)}>
          <section role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <span><Trash2 size={22} /></span><h2>V2 şarkı silinsin mi?</h2><p><strong>{song.title}</strong> kalıcı olarak silinecek.</p>
            <div><button type="button" onClick={() => setDeleteOpen(false)} disabled={working}>Vazgeç</button><button type="button" onClick={remove} disabled={working}><Trash2 size={15} /> {working ? "Siliniyor" : "Şarkıyı sil"}</button></div>
          </section>
        </div>
      )}
    </div>
  );
}
